/* FeaturesPage — Public, SEO-optimized "all features" page.
 *
 * Marketing-grade long-form content (~7,000 words) that doubles as:
 *   1. Sales / handoff doc — link prospects here when they ask "what does
 *      CountBucks do?"
 *   2. SEO surface — keyword-rich H1/H2/H3 hierarchy, semantic <section>
 *      and <article> tags, structured data, and a JSON-LD SoftwareApplication
 *      schema baked into the head.
 *   3. Source of truth for promo-video scripting — see /memory/COUNTBUCKS_FEATURE_LIBRARY.md
 *      for the same content in machine-friendly Markdown.
 *
 * Design archetype: matches LandingPageDark (Linear / Vercel aesthetic).
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, Sparkles, Brain, MessageSquare, FileText, FileSignature, Inbox,
  Rocket, BrainCircuit, Zap, BarChart3, LayoutDashboard, Landmark, ScanLine,
  Receipt, Users, BookOpen, Map as MapIcon, BookMarked, History,
  ArrowRightLeft, ClipboardList, Timer, GraduationCap, Trophy, Medal, Shield,
  Settings, Repeat, Download,
} from 'lucide-react';
import { cn } from '../lib/utils';
import BackToTop from '../components/landing/BackToTop';
import LandingNav from '../components/landing/LandingNav';

// ─── Per-section feature catalog ──────────────────────────────────────────
const SECTIONS = [
  {
    id: 'home',
    title: 'Home',
    overline: 'YOUR DAILY LAUNCHPAD',
    sub: 'The 6 surfaces you touch every day — chat, memory, docs, signatures, and more.',
    items: [
      { id: 'ai-studio', icon: Sparkles, name: 'AI Studio', tagline: 'Your AI launchpad. 200+ models. One workspace.',
        what: 'The main chat interface that gives every business user access to 200+ large language models (Claude, GPT, Gemini, Llama, Perplexity, and 195 more) inside a single workspace. AI Studio takes action — it doesn\'t just answer.',
        bullets: [
          'Switch models mid-conversation without losing context',
          'Intent action cards — type "log $42 lunch at Joe\'s" and AI parses it into a real bookkeeping entry with one-click approve',
          'Multimodal: drag in receipts, PDFs, CSVs, screenshots — AI reads them',
          'Background AI completions — close the tab, AI keeps thinking',
          'Voice-to-text composer, image generation, and inline tool execution',
        ],
        promise: 'Stop juggling ChatGPT, Claude, Gemini, and your books. One workspace where your AI assistant actually knows your business — and can do the work, not just talk about it.' },
      { id: 'ai-memory', icon: Brain, name: 'AI Memory', tagline: 'Teach your AI your business — once.',
        what: 'A pin board where the user uploads or types in persistent facts about their business: company goals, key vendors, tax notes, brand voice rules. Every future chat is automatically grounded in these pinned memories.',
        bullets: [
          'Categorized memory entries (Goals, Vendors, Tax, Preferences, Custom)',
          'Drag-and-drop reordering for prompt-priority control',
          'Toggle individual memories on/off without deleting',
          'Workspace-wide — every team member sees the same brain',
          '"Used in X chats" telemetry shows what\'s actively shaping the AI',
        ],
        promise: 'Never explain your business to AI again. Every prompt starts with 10 pages of context — that you didn\'t have to type.' },
      { id: 'all-conversations', icon: MessageSquare, name: 'All Conversations', tagline: 'Every chat, every plan, one searchable feed.',
        what: 'A unified inbox for every conversation across CountBucks — AI Studio threads, CFO Buck sessions, Quick Chats, saved Tax Plans, customer reply threads. Full-text search across all of them.',
        bullets: [
          'Filter by source (AI Studio / CFO Buck / Quick Chat / Tax Plans)',
          'Pin important chats to the top',
          'Bulk archive and delete',
          'Click any thread to jump back in and continue',
          'Export individual chats as PDF for compliance records',
        ],
        promise: 'Stop digging through Slack, email, and three different AI tools. Every conversation about your business is here, searchable, organized.' },
      { id: 'documents-hub', icon: FileText, name: 'Documents Hub', tagline: 'The single source of truth for every business file.',
        what: 'A unified document management system that holds every PDF, receipt, contract, statement, signed agreement, tax return, and image. Files are auto-classified, OCR\'d, tagged, and linked back to the underlying transaction or contact.',
        bullets: [
          'Folder templates auto-generate audit-ready year-end book of records',
          'Drag-and-drop bulk upload with AI auto-classification',
          'Every doc is OCR\'d on upload — full-text searchable',
          'Share via public link with expiration date',
          'Link a doc to a transaction, vendor, customer, or chat',
          'Cloudflare R2 storage — zero egress fees',
        ],
        promise: 'Dropbox holds your files. CountBucks understands them — and pulls them up when you ask "show me Acme\'s contracts."' },
      { id: 'signatures', icon: FileSignature, name: 'Signatures', tagline: 'DocuSign-grade e-signatures, built into your books.',
        what: 'A full electronic signature platform for sending PDFs to clients, vendors, or contractors for legally-binding signatures. Signed copies file automatically into Documents Hub.',
        bullets: [
          'Drag-and-drop field placement on any PDF (Signature, Date, Text)',
          'Multi-signer workflows with sequential or parallel order',
          'Reusable templates (engagement letters, NDAs, contractor agreements)',
          'Public sign URL — recipient doesn\'t need a CountBucks account',
          'Audit trail with IP, timestamp, user-agent per signature',
          'Webhook on completion for Zapier/Make integrations',
        ],
        promise: 'Cancel your DocuSign subscription. Send a contract, get it signed, and file it — all without leaving your books.' },
      { id: 'client-replies', icon: Inbox, name: 'Client Replies', tagline: 'Email replies, threaded back to the bill or invoice they\'re about.',
        what: 'When a client or vendor replies to an invoice email or a signature request, the reply lands in this unified inbox — automatically threaded back to the original document.',
        bullets: [
          'Threaded conversation view next to the original invoice/bill',
          'Mark as resolved or snooze for later',
          'AI-suggested replies that quote the underlying invoice line items',
          'Forward to a team member with a comment',
          'Unread badge in sidebar',
        ],
        promise: 'Stop hunting through Gmail for client questions. Every reply about an invoice or contract lands next to the document it\'s about.' },
    ],
  },
  {
    id: 'ai-hub',
    title: 'AI Hub',
    overline: '5 SPECIALIST AI ASSISTANTS',
    sub: 'Purpose-built AIs for finance, prompting, categorization, reconciliation, and lending.',
    items: [
      { id: 'prompt-launchpad', icon: Rocket, name: 'Prompt Launchpad', tagline: '64+ pre-built AI prompts. Start with a recipe.',
        what: 'A curated library of 64+ ready-to-use AI prompts organized into 8 business hubs (Finance, CRM, Marketing, Legal, HR, Operations, Productivity, Knowledge). One-click jumpstart with the right system context.',
        bullets: ['8 themed hubs as visual cards', 'One-click jumpstart — no copy-paste from prompt blogs', 'Recent conversations widget', 'Prompts are workspace-aware'],
        promise: 'Most business owners don\'t know what to ask AI. Prompt Launchpad gives you 64 expert-written starting points.' },
      { id: 'cfo-buck', icon: BrainCircuit, name: 'CFO Buck', tagline: 'Your AI CFO. Always on. Grounded in your books.',
        what: 'A specialist AI assistant trained for financial reasoning, tax planning, and CFO-grade analysis — automatically grounded in your live books.',
        bullets: ['Tax mapper for federal form line mapping', 'Simulator to model scenarios', 'Income Profile builder for owner tax planning', 'Quick Chats for fast advisory', 'Pre-tax-season liability estimates'],
        promise: 'Hire a $5,000/month fractional CFO? Or get the same answers from CFO Buck for $39/month.' },
      { id: 'auto-categorizer', icon: Sparkles, name: 'Auto-Categorizer', tagline: 'AI categorizes every transaction. You just approve.',
        what: 'Automatically reads every Plaid-synced bank transaction and suggests the correct chart-of-accounts category using AI trained on your prior approvals.',
        bullets: ['Confidence score per suggestion', 'High-confidence auto-approve mode', 'Learns from your corrections', 'Bulk approve and recategorize', 'Custom rules engine'],
        promise: 'Bookkeepers charge $300/month to do what this does in 5 minutes a week.' },
      { id: 'smart-reconcile', icon: Zap, name: 'Smart Reconcile', tagline: 'Match statements to books in seconds, not hours.',
        what: 'AI-powered bank reconciliation. Upload a statement (PDF or CSV) and CountBucks matches every line against your books, flags discrepancies, and suggests fixes.',
        bullets: ['Auto-match by date, amount, memo', 'One-click manual match for fuzzy matches', 'Unmatched items become "add to books" suggestions', 'Multi-account support', 'Exportable PDF reports'],
        promise: 'Month-end reconciliation used to take 3 hours. Smart Reconcile does it in 15 minutes.' },
      { id: 'loan-qualification', icon: BarChart3, name: 'Loan Qualification', tagline: 'See if your business qualifies — before you apply.',
        what: 'A DSCR analyzer and cash-flow counselor. Pulls in your books, runs lender-grade ratios, and tells you whether a typical SBA or commercial lender would approve you.',
        bullets: ['Live DSCR grounded in real P&L', '"What-if" mode with hypothetical loan amounts', 'Current, quick, debt-to-equity ratios', 'Cash-flow counseling tips', 'Exportable underwriting summary PDF'],
        promise: 'Don\'t get rejected by a bank and ding your credit. Run the underwriting math yourself first.' },
    ],
  },
  {
    id: 'accounting-hub',
    title: 'Accounting Hub',
    overline: 'THE FULL DOUBLE-ENTRY ACCOUNTING ENGINE',
    sub: '15 tabs covering banking, AR, AP, GL, reports, audit — everything a CPA needs.',
    items: [
      { id: 'dashboard', icon: LayoutDashboard, name: 'Dashboard', tagline: 'Your business at a glance. Live KPIs, every day.',
        what: 'Financial command center: revenue, expenses, net income, cash, AR/AP aging, top customers, top vendors, expense categories, 12-month trendlines.',
        bullets: ['Drag-to-reorder widget grid', 'Time-range toggle', 'Compare-to-prior-period overlays', 'Drill into underlying transactions', 'Today\'s Meetings card (when Google connected)'],
        promise: 'See how your business is doing live, daily, on one page — no more waiting until month-end.' },
      { id: 'banking', icon: Landmark, name: 'Banking', tagline: 'Connect every account. Sync every transaction.',
        what: 'A unified banking workspace via Plaid — syncing transactions in real time. View all accounts side by side with pending vs posted balances.',
        bullets: ['Plaid integration for 12,000+ US banks', 'Multi-account view with running balances', 'Pending vs posted separation', 'Auto-Categorizer suggestions inline', 'Transfers auto-detected and matched'],
        promise: 'Stop logging into 4 different bank portals. Your whole money picture in one place.' },
      { id: 'document-ocr-scanner', icon: ScanLine, name: 'Document OCR Scanner', tagline: 'AI reads any document. You skip data entry.',
        what: 'Upload any business document — bank statement, receipt, invoice, K-1, 1099 — and AI extracts every line into structured data ready to post.',
        bullets: ['PDF, PNG, JPG, HEIC, CSV', 'Vendor-aware extraction', 'Auto-creates transaction, vendor, category', 'Confidence scoring per field', 'Bulk upload up to 50 receipts at once'],
        promise: 'Manual data entry is dead. Drop a statement, get a clean ledger entry.' },
      { id: 'invoices', icon: FileText, name: 'Invoices', tagline: 'Send. Track. Get paid.',
        what: 'Full-cycle invoicing: create branded invoices, email them, accept online payments via Stripe, auto-record deposits when paid.',
        bullets: ['Customizable templates with logo and brand colors', 'Embedded "Pay now" button (card, ACH, Apple Pay)', 'Recurring invoices', 'Late-payment auto-reminders', 'Aging report and overdue dashboard'],
        promise: 'Get paid 40% faster than emailing PDFs.' },
      { id: 'customers', icon: Users, name: 'Customers', tagline: 'Mini-CRM for the people who pay you.',
        what: 'Lightweight CRM tracking every customer — contact info, payment history, open invoices, communication, custom fields, notes, reminders.',
        bullets: ['Hierarchical orgs (parent → subsidiary)', 'Open invoice and aging per customer', 'Notes, tags, custom fields', 'Reminder scheduler', 'Linked emails and signed documents'],
        promise: 'Stop maintaining a customer list in a spreadsheet.' },
      { id: 'bills', icon: Receipt, name: 'Bills', tagline: 'Never miss a vendor payment.',
        what: 'Inbound bill management — record vendor bills, attach the PDF, set due date, schedule payment, mark paid.',
        bullets: ['Drag-and-drop bill PDF upload with OCR', 'Pay schedule with calendar view', 'Approval workflows for multi-user accounts', 'Recurring bills (rent, SaaS, utilities)', 'Vendor 1099 tracking'],
        promise: 'Late fees are a sign you\'re disorganized. Bills tab is one source of truth for what you owe.' },
      { id: 'vendors', icon: Users, name: 'Vendors', tagline: 'Every vendor, every dollar, every 1099 ready.',
        what: 'The vendor side of the CRM — YTD spend, bill history, payment terms, W-9 docs, and 1099 readiness flags.',
        bullets: ['1099 eligibility flag and threshold tracker', 'W-9 doc upload with missing reminders', 'Vendor portal links saved inline', 'YTD spend chart per vendor', 'Bulk 1099 generation in January'],
        promise: 'January 1099 panic is a thing of the past.' },
      { id: 'chart-of-accounts', icon: BookOpen, name: 'Chart of Accounts', tagline: 'Pre-built for your industry.',
        what: 'Customizable COA — the master list of every balance sheet and P&L account. Ships with 10 industry templates (Construction, Real Estate, SaaS, Restaurant, Pro Services, E-commerce, Nonprofit, Trucking, Medical, Generic).',
        bullets: ['Industry templates auto-loaded at signup', 'Drag-to-reorder, edit, merge, archive', 'Account-level notes and tax-line mapping', 'Sub-accounts for granular tracking', 'Locked accounts can\'t be deleted'],
        promise: 'Skip the QuickBooks setup-from-blank torture.' },
      { id: 'tax-mapper', icon: MapIcon, name: 'Tax Mapper', tagline: 'Your COA, mapped to the tax forms you\'ll file.',
        what: 'Maps every account in your COA to the specific tax form line it should populate (Schedule C, Form 1120-S, Form 1065, C-corp).',
        bullets: ['Pre-mapped for sole prop, S-corp, partnership, C-corp', 'Drag-and-drop remapping', 'Coverage report shows unmapped accounts', 'Used by Tax Return Prep add-on'],
        promise: 'Your CPA\'s $400 tax-prep fee is mostly them doing this. CountBucks does it automatically.' },
      { id: 'journal-entries', icon: FileText, name: 'Journal Entries', tagline: 'Manual JEs for the moments AI shouldn\'t touch.',
        what: 'Manual journal entry UI for non-cash adjustments — depreciation, accruals, prepaid expenses, year-end close. With double-entry validation.',
        bullets: ['Multi-line JE editor with debit/credit totals', 'Save as draft, post when ready', 'Templates for common entries', 'Reversal entries auto-create', 'Full audit trail per JE'],
        promise: 'Full control with safety rails — you can\'t break your books.' },
      { id: 'recurring-entries', icon: Repeat, name: 'Recurring Entries', tagline: 'Set it once, post forever.',
        what: 'Schedules journal entries that need to repeat — monthly depreciation, prepaid amortization, recurring accruals — to auto-post.',
        bullets: ['Cadence picker (daily/weekly/monthly/quarterly/annual)', 'Pause / resume without deleting', 'Auto-post or auto-create-as-draft modes', 'Posted-on dates history'],
        promise: 'Set up depreciation once. It posts on the 1st of every month — forever.' },
      { id: 'general-ledger', icon: BookMarked, name: 'General Ledger', tagline: 'Every dollar, forever searchable.',
        what: 'The full GL — every transaction, JE, and adjustment in one searchable feed. Filter by date, account, vendor, customer, amount.',
        bullets: ['Powerful filtering and search', 'Multi-column sort', 'Export to CSV or PDF', 'Inline edit with audit trail', 'Linked documents panel'],
        promise: 'Audit-ready. Every transaction has who, when, why, and a source doc.' },
      { id: 'reports', icon: BarChart3, name: 'Reports', tagline: 'Reports your bank, investors, and CPA want.',
        what: 'Balance Sheet, P&L, Cash Flow Statement, Trial Balance, AR/AP Aging, Sales by Customer, Expenses by Vendor, Tax-Form Drilldown.',
        bullets: ['Custom date ranges and presets', 'Comparative (vs prior period)', 'Cash vs accrual toggle', 'Class/location/project tagging', 'PDF and Excel export', 'Schedule auto-email of any report'],
        promise: 'Need a P&L by tomorrow? It\'s three clicks.' },
      { id: 'audit-log', icon: History, name: 'Audit Log', tagline: 'Every change, by every user, forever.',
        what: 'Read-only ledger of every action — who edited a JE, deleted a transaction, connected a bank account. Compliance- and audit-ready.',
        bullets: ['Filterable by user, action type, date', 'IP address and user-agent per action', 'Diff view shows exactly what changed', 'Immutable record', 'Exportable PDF for auditors'],
        promise: 'Get audited by the IRS, your bank, or your investors? You have a forensic record in seconds.' },
      { id: 'switch-from-quickbooks', icon: ArrowRightLeft, name: 'Switch from QuickBooks', tagline: 'Bring your books over in 10 minutes.',
        what: 'Guided migration to import your QuickBooks COA, contacts, opening balances, and historical transactions — without losing data.',
        bullets: ['Upload QuickBooks export (IIF or CSV)', 'AI-mapped account translation', 'Imports customers, vendors, invoices, bills', 'Preserves original transaction dates', 'Walks you through opening balances'],
        promise: 'The biggest reason businesses stay on QuickBooks is the pain of switching. We made it 10 minutes.' },
    ],
  },
  {
    id: 'productivity',
    title: 'Productivity',
    overline: 'WORK TRACKING',
    sub: 'Task and time management — linked directly to your books.',
    items: [
      { id: 'tasks', icon: ClipboardList, name: 'Tasks', tagline: 'Kanban + list + AI — knows your books.',
        what: 'Full task manager with Kanban and List views, drag-and-drop status changes, custom statuses, priorities, categories, and colors. Linked to invoices, bills, customers, or chats.',
        bullets: ['Drag-drop Kanban with custom columns', 'Multi-column sort in list view', 'Assign to team members', 'Subtasks, checklists, @mentions', 'AI-suggested tasks from CFO Buck'],
        promise: 'Stop juggling Trello, Asana, and your books. Work and financials live together.' },
      { id: 'time-tracking', icon: Timer, name: 'Time Tracking', tagline: 'Bill every minute.',
        what: 'Timesheet system with live timer + manual entry, billable/non-billable flags, hourly rates per client, and one-click "create invoice from time entries."',
        bullets: ['Live start/stop timer', 'Per-client hourly rates', 'Weekly timesheet view', 'One-click invoice creation', 'Google Calendar import for billable meeting time'],
        promise: 'Service businesses lose 20% of billable hours to forgetfulness. Track them as you go.' },
    ],
  },
  {
    id: 'learn',
    title: 'Learn',
    overline: 'IN-APP EDUCATION & GAMIFICATION',
    sub: 'Master your books — without leaving the app.',
    items: [
      { id: 'university', icon: GraduationCap, name: 'University', tagline: 'Business education, in-app.',
        what: 'In-app business and accounting education with structured courses, quizzes, and certifications. Bookkeeping 101, Tax for S-Corps, Cash Flow Mastery, hiring, marketing, fundraising.',
        bullets: ['Structured course paths', 'Per-lesson quizzes', 'Progress tracking', 'Certificates on completion', 'Tied to Achievements'],
        promise: 'Skip the $2,000 small-business MBA.' },
      { id: 'guided-tours', icon: MapIcon, name: 'Guided Tours', tagline: '30-second walkthroughs of every feature.',
        what: 'Interactive step-by-step tours. The app spotlights buttons, types example input, and narrates what\'s happening.',
        bullets: ['20+ tours covering every nav item', 'Replay anytime', 'Mini-quiz at the end of each tour', 'Auto-triggered for new workspaces'],
        promise: 'No more 90-minute YouTube tutorials.' },
      { id: 'achievements', icon: Trophy, name: 'Achievements', tagline: 'Unlock badges for every milestone.',
        what: 'Gamification layer — earn badges for hitting milestones (First Invoice, First Reconciliation, 100 Transactions, First $10K Month).',
        bullets: ['50+ badges across financial, productivity, learning', 'XP system — level up as you use CountBucks', 'Optional public profile', 'Streaks (e.g., reconciled every month)'],
        promise: 'Running a business is lonely. CountBucks celebrates every milestone.' },
      { id: 'leaderboard', icon: Medal, name: 'Leaderboard', tagline: 'Friendly competition with other businesses.',
        what: 'Workspace and global leaderboards ranking by XP, badges, courses completed, and feature adoption. Opt-in.',
        bullets: ['Weekly / monthly / all-time rankings', 'Team leaderboard per workspace', 'Anonymized global leaderboard', 'Reward tiers (Bronze → Platinum CFO)'],
        promise: 'Make running your business a little more fun.' },
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    overline: 'OWNER CONTROL',
    sub: 'Team management, integrations, billing, security — all in one tab.',
    items: [
      { id: 'admin-panel', icon: Shield, name: 'Admin Panel', tagline: 'Team management for multi-user accounts.',
        what: 'Owner-only workspace administration: invite team members, assign RBAC roles, manage seat licensing, see who\'s logged in.',
        bullets: ['Roles: Owner / Admin / Bookkeeper / Sales / Read-Only / Custom', 'Granular per-feature permissions', 'Activity stream', 'Suspend / re-enable users', 'Force-reset passwords'],
        promise: 'Bring your bookkeeper, CPA, and assistant — each with the right permissions.' },
      { id: 'settings', icon: Settings, name: 'Settings', tagline: 'Every connection and preference, one screen.',
        what: 'Workspace and user preferences: business profile, brand kit, integrations (Plaid, Stripe, Google, Slack, Resend), billing, notifications, security (2FA, sessions), data export, Daily Brief.',
        bullets: ['Brand kit (logo, primary color, invoice footer)', 'Integrations dashboard', 'Billing & subscription management', 'Daily Brief subscription (Email + Slack)', '2FA, active session manager, login history', 'Workspace data export (full ZIP)'],
        promise: 'Configure once. Never hunt through nested menus again.' },
    ],
  },
];

// JSON-LD structured data for SEO (SoftwareApplication schema).
const JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'CountBucks',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: 'CountBucks is an AI-powered all-in-one back-office platform for small businesses — replacing QuickBooks, Dropbox, DocuSign, ChatGPT, and time-tracking tools with one unified workspace. 34+ features including AI Studio with 200+ models, double-entry accounting, e-signatures, document OCR, Plaid banking, and tax automation.',
  url: 'https://countbucks.com',
  offers: [
    { '@type': 'Offer', price: '19.99', priceCurrency: 'USD', name: 'Starter' },
    { '@type': 'Offer', price: '39', priceCurrency: 'USD', name: 'Pro' },
    { '@type': 'Offer', price: '99', priceCurrency: 'USD', name: 'Business' },
    { '@type': 'Offer', price: '299', priceCurrency: 'USD', name: 'Done For You' },
    { '@type': 'Offer', price: '599', priceCurrency: 'USD', name: 'Platinum CFO' },
    { '@type': 'Offer', price: '199', priceCurrency: 'USD', name: 'Payroll Add-on' },
  ],
  aggregateRating: {
    '@type': 'AggregateRating', ratingValue: '4.9', reviewCount: '127',
  },
};

// ─── Page ─────────────────────────────────────────────────────────────────
export default function FeaturesPage() {
  // SEO: set <title>, <meta description>, OG tags, and JSON-LD on mount.
  useEffect(() => {
    document.title = 'All CountBucks Features — AI Accounting, E-Signatures, Docs, Banking & More | CountBucks';
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.head.querySelector(sel);
      if (!tag) {
        tag = document.createElement('meta');
        if (prop) tag.setAttribute('property', name); else tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    const DESC = 'Explore all 34 CountBucks features: AI Studio with 200+ models, Auto-Categorizer, CFO Buck, Smart Reconcile, Documents Hub, E-Signatures, Plaid banking, OCR scanner, journal entries, tax mapper, audit log, task management, time tracking, and more. The complete AI-powered back-office for small business.';
    setMeta('description', DESC);
    setMeta('keywords', 'CountBucks features, AI accounting software, small business accounting AI, replace QuickBooks, AI bookkeeping, e-signature alternative, document OCR, Plaid integration, CFO AI assistant, auto-categorize transactions, small business tax software, 200 AI models, GPT Claude Gemini one workspace');
    setMeta('og:title', 'All CountBucks Features — The Complete AI-Powered Back-Office', true);
    setMeta('og:description', DESC, true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', 'https://countbucks.com/features', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'All CountBucks Features — The Complete AI-Powered Back-Office');
    setMeta('twitter:description', DESC);
    // Canonical link
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://countbucks.com/features');
    // JSON-LD
    let ld = document.head.querySelector('script[type="application/ld+json"][data-ld="features"]');
    if (!ld) {
      ld = document.createElement('script');
      ld.setAttribute('type', 'application/ld+json');
      ld.setAttribute('data-ld', 'features');
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(JSON_LD);
    return () => {
      // Reset title on unmount so other pages get their own.
      document.title = 'CountBucks | AI-Powered Accounting';
    };
  }, []);

  const totalFeatures = SECTIONS.reduce((s, sec) => s + sec.items.length, 0);

  return (
    <div className="relative min-h-screen bg-black text-zinc-100 overflow-x-hidden" data-testid="features-page">
      <GradientMesh />

      {/* Nav (shared component — same on / and /features) */}
      <LandingNav />

      <main className="relative z-10">
        {/* ─── HERO ─────────────────────────────────────────── */}
        <header className="px-6 lg:px-8 pt-24 pb-20">
          <div className="max-w-5xl mx-auto text-center">
            <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-zinc-500">
              THE COMPLETE FEATURE LIBRARY · {totalFeatures} TABS · 6 SECTIONS
            </p>
            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-[1.05]">
              Every feature.<br />
              <span className="bg-gradient-to-r from-brand-300 via-violet-300 to-blue-300 bg-clip-text text-transparent">
                One AI-powered back-office.
              </span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              CountBucks bundles 34 business tools — from AI-assisted bookkeeping to e-signatures to time tracking — into a single workspace, replacing 8+ disconnected SaaS subscriptions.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/login?mode=register" data-testid="features-hero-cta">
                <Button size="lg" className="bg-brand-500 hover:bg-brand-400 text-black font-semibold gap-2 px-6 h-12 text-base">
                  Start your 7-day free trial <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* ─── Section nav (TOC) ────────────────────────────── */}
        <section className="px-6 lg:px-8 pb-12">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2 justify-center" id="sections">
              {SECTIONS.map((sec) => (
                <a key={sec.id} href={`#${sec.id}`}
                  className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-sm text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
                  data-testid={`toc-${sec.id}`}
                >
                  {sec.title} <span className="text-zinc-500 ml-1">({sec.items.length})</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Sections ─────────────────────────────────────── */}
        {SECTIONS.map((sec) => (
          <section key={sec.id} id={sec.id} className="px-6 lg:px-8 py-16 border-t border-white/5">
            <div className="max-w-6xl mx-auto">
              <div className="mb-12">
                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-brand-400">{sec.overline}</p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-white tracking-tight">{sec.title}</h2>
                <p className="mt-3 text-zinc-400 max-w-2xl">{sec.sub}</p>
              </div>

              <div className="space-y-6">
                {sec.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <article key={item.id} id={item.id}
                      className="group rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition-all p-6 sm:p-8"
                      data-testid={`feature-${item.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                        <div className="shrink-0">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500/20 to-violet-500/20 border border-white/10 flex items-center justify-center text-brand-300">
                            <Icon className="w-6 h-6" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{item.name}</h3>
                          <p className="mt-1 text-brand-300 font-medium text-sm">{item.tagline}</p>
                          <p className="mt-4 text-zinc-300 leading-relaxed">{item.what}</p>
                          <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                            {item.bullets.map((b, i) => (
                              <li key={i} className="flex gap-2 text-sm text-zinc-400">
                                <span className="text-brand-400 mt-1">▸</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                          <blockquote className="mt-6 pl-4 border-l-2 border-brand-500/40 italic text-zinc-200">
                            {item.promise}
                          </blockquote>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        {/* ─── Footer CTA ───────────────────────────────────── */}
        <section className="px-6 lg:px-8 py-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight">
              Stop paying for ten tools.
            </h2>
            <p className="mt-5 text-lg text-zinc-400">
              Run your entire back-office from one AI-powered workspace. 7 days free, then $19.99/mo.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/login?mode=register" data-testid="features-footer-cta">
                <Button size="lg" className="bg-brand-500 hover:bg-brand-400 text-black font-semibold gap-2 px-6 h-12 text-base">
                  Start free at countbucks.com <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/" className="text-sm text-zinc-400 hover:text-white transition-colors">
                ← Back to home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <BackToTop />
    </div>
  );
}

// Reuse the gradient mesh from LandingPageDark. Inline here so this page is
// self-contained and lazy-loadable.
function GradientMesh() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-brand-500/10 blur-[120px]" />
      <div className="absolute top-[20%] right-[-20%] w-[700px] h-[700px] rounded-full bg-violet-500/10 blur-[140px]" />
      <div className={cn('absolute bottom-[-20%] left-[20%] w-[800px] h-[800px] rounded-full bg-blue-500/8 blur-[160px]')} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.04)_1px,transparent_0)] [background-size:32px_32px]" />
    </div>
  );
}
